import type { ComponentResponse, OperationRequest } from '../api/types';
import type { OperationBuilders } from '../state/operations';

/**
 * The to-do list's wire convention (ADR-029 §3) — a client-side convention over the plain
 * component model, never a server rule: a `todo-list` block with a `title`, its `task` children
 * in array order, and a task is done when it carries `done: "true"` (any other value or no key
 * is not done). Ticking writes the key, unticking removes it (`edit` with null) — per-key last
 * writer wins, so two people toggling different tasks never clash.
 */
export const TODO_LIST_TYPE = 'todo-list';
export const TASK_TYPE = 'task';
export const DONE_KEY = 'done';
export const DONE_VALUE = 'true';

/** The block's display title — the id until it has one. */
export function titleOf(component: ComponentResponse): string {
  return component.properties['title'] ?? component.id;
}

/** Whether a task carries the done mark. */
export function isDone(task: ComponentResponse): boolean {
  return task.properties[DONE_KEY] === DONE_VALUE;
}

/** The list's tasks, in array order (other child types are not tasks and are left out). */
export function tasksOf(list: ComponentResponse): ComponentResponse[] {
  return list.children.filter((child) => child.type === TASK_TYPE);
}

/** The to-do list's mutations, each one wire operation. */
export interface TodoOperations {
  /** A new list, appended at the top level or under `parentId`. */
  addTodoList(title: string, parentId?: string): OperationRequest;
  /** A new task at the end of the list. */
  addTask(listId: string, title: string): OperationRequest;
  /** Ticks (`done: "true"`) or unticks (the key removed) a task. */
  setDone(taskId: string, done: boolean): OperationRequest;
  renameTask(taskId: string, title: string): OperationRequest;
  /** Moves a task to `index` among the list's children. */
  moveTask(listId: string, taskId: string, index: number): OperationRequest;
  removeTask(taskId: string): OperationRequest;
}

/** The to-do mutations over the plain operation builders. */
export function createTodoOperations(ops: OperationBuilders): TodoOperations {
  return {
    addTodoList: (title, parentId) => ops.makeAdd(TODO_LIST_TYPE, title, parentId),
    addTask: (listId, title) => ops.makeAdd(TASK_TYPE, title, listId),
    setDone: (taskId, done) => ops.makeEdit(taskId, DONE_KEY, done ? DONE_VALUE : null),
    renameTask: (taskId, title) => ops.makeEdit(taskId, 'title', title),
    moveTask: (listId, taskId, index) => ops.makeMove(taskId, index, listId),
    removeTask: (taskId) => ops.makeRemove(taskId),
  };
}
